import { apiClient } from "./api-client";

export interface FaqEntry {
  id: number;
  question: string;
  answer: string;
  category: string | null;
  orderIndex: number;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FaqContent {
  lastUpdatedAt: string | null;
  count: number;
  items: FaqEntry[];
}

export interface FaqNotModified {
  notModified: true;
  lastUpdatedAt: string | null;
}

export interface FaqMeta {
  lastUpdatedAt: string | null;
  count: number;
}

export interface CreateFaqEntryPayload {
  question: string;
  answer: string;
  category?: string;
  orderIndex?: number;
  isPublished?: boolean;
}

export type UpdateFaqEntryPayload = Partial<CreateFaqEntryPayload>;

export const isFaqNotModified = (
  value: FaqContent | FaqNotModified,
): value is FaqNotModified =>
  (value as FaqNotModified).notModified === true;

export const faqApi = {
  /**
   * FAQ publique. Passer `since` (ISO) pour ne recevoir le contenu que s'il a
   * changé depuis la dernière synchro.
   */
  async getFaq(since?: string): Promise<FaqContent | FaqNotModified> {
    const { data } = await apiClient.get("/faq", {
      params: since ? { since } : undefined,
    });
    return data;
  },

  async getMeta(): Promise<FaqMeta> {
    const { data } = await apiClient.get("/faq/meta");
    return data;
  },

  // --- Admin ---
  async listAll(): Promise<{ count: number; items: FaqEntry[] }> {
    const { data } = await apiClient.get("/faq/admin");
    return data;
  },

  async create(payload: CreateFaqEntryPayload): Promise<FaqEntry> {
    const { data } = await apiClient.post("/faq", payload);
    return data;
  },

  async update(
    id: number,
    payload: UpdateFaqEntryPayload,
  ): Promise<FaqEntry> {
    const { data } = await apiClient.patch(`/faq/${id}`, payload);
    return data;
  },

  async remove(id: number): Promise<{ deleted: boolean; id: number }> {
    const { data } = await apiClient.delete(`/faq/${id}`);
    return data;
  },
};
